"use client";

import { ReactNode, useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

/**
 * Each child card has scroll-linked 3D perspective tilt and parallax.
 * Cards at different depths create a cinematic layered effect.
 */
interface ScrollCard3DProps {
    children: ReactNode;
    index: number;
    total: number;
    className?: string;
}

export function ScrollCard3D({
    children,
    index,
    total,
    className,
}: ScrollCard3DProps) {
    const ref = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    // Each card has slightly different parallax based on index
    const parallaxOffset = ((index - total / 2) / total) * 80;
    const y = useSpring(
        useTransform(scrollYProgress, [0, 0.5, 1], [parallaxOffset + 60, 0, -parallaxOffset - 60]),
        { stiffness: 80, damping: 25 }
    );

    // Subtle rotation based on scroll
    const rotateY = useSpring(
        useTransform(scrollYProgress, [0, 0.5, 1], [index % 2 === 0 ? -5 : 5, 0, index % 2 === 0 ? 5 : -5]),
        { stiffness: 80, damping: 25 }
    );

    const rotateX = useSpring(
        useTransform(scrollYProgress, [0, 0.5, 1], [8, 0, -8]),
        { stiffness: 80, damping: 25 }
    );

    // Scale: centered position is 1, edges are smaller
    const scale = useSpring(
        useTransform(scrollYProgress, [0, 0.3, 0.5, 0.7, 1], [0.85, 0.95, 1, 0.95, 0.85]),
        { stiffness: 80, damping: 25 }
    );

    // Opacity: visible in center
    const opacity = useSpring(
        useTransform(scrollYProgress, [0, 0.2, 0.5, 0.8, 1], [0.3, 0.8, 1, 0.8, 0.3]),
        { stiffness: 100, damping: 30 }
    );

    return (
        <motion.div
            ref={ref}
            style={{
                y,
                rotateY,
                rotateX,
                scale,
                opacity,
                transformPerspective: 1200,
                transformOrigin: "center center",
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}
